import { useRef, useEffect, useState, useCallback } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TruncatedTextTooltipProps {
  text: string;
  className?: string;
  children?: React.ReactNode;
}

export function TruncatedTextTooltip({ 
  text, 
  className = "", 
  children 
}: TruncatedTextTooltipProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, [text]);

  useEffect(() => {
    checkOverflow();
    
    // Add resize listener to recheck overflow on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  const content = children || text;

  if (!isOverflowing) {
    return (
      <div ref={textRef} className={`truncate ${className}`}>
        {content}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div 
            ref={textRef} 
            className={`truncate cursor-help ${className}`}
            aria-label={`Full text: ${text}`}
            tabIndex={0}
          >
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" avoidCollisions>
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
